import { useCallback } from 'react'
import { useRecoilCallback } from 'recoil'
import { updateCase } from '@/api/cases'
import type { NameArrangement } from '@messenger/types'
import { caseAtom, caseValuesAtom } from 'atoms'
import {
  EDITOR_FIELD_NAME_OF_DECEASED,
  caseColumnsWithTabularRows,
  fieldKeyToDateColumn,
  fieldKeyToWritableCaseField,
  nameOfDeceasedFromCase,
  nameOfDeceasedPartColumns,
  parseServiceAuthorColumn,
  tabularRowForm,
  trimWrittenCaseValue,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type { TabularRow } from '@/utils/editor-text-box/editorTextBoxAdapter'

const NAME_PART_COLUMNS: ReadonlySet<string> = nameOfDeceasedPartColumns()

type CaseServiceRow = Record<string, unknown> & { id?: unknown }

/** A scalar field's new value, or a date field's new date. */
export type CaseFieldValueWrite = { fieldKey: string; value: string | Date }
/** A tabular field's rows, as its row form holds them. */
export type CaseFieldRowsWrite = {
  fieldKey: string
  rows: readonly TabularRow[]
  /** The additional-family group the rows are; unset, the field's whole column. */
  rowType?: string
}
/**
 * The name of deceased's arrangement, the one the case's name boxes built
 * from name parts follow; `docs/features/text-editor.md` names the exceptions.
 */
export type CaseFieldArrangementWrite = {
  fieldKey: string
  arrangement: NameArrangement
}
export type CaseFieldWrite =
  | CaseFieldValueWrite
  | CaseFieldRowsWrite
  | CaseFieldArrangementWrite

/** The case columns one write changes, each with what it stores. */
type ResolvedWrite = Record<string, unknown>

const servicesWithAuthoredColumn = (
  caseRecord: Record<string, unknown>,
  serviceId: string,
  column: string,
  stored: string | Date,
): CaseServiceRow[] | null => {
  const rows = caseRecord.services
  if (!Array.isArray(rows)) return null
  const index = rows.findIndex(
    (row) => (row as CaseServiceRow | null)?.id === serviceId,
  )
  if (index < 0) return null
  return rows.map((row, i) =>
    i === index
      ? { ...(row as CaseServiceRow), [column]: stored }
      : (row as CaseServiceRow),
  )
}

export const resolveWrite = (
  caseRecord: Record<string, unknown>,
  write: CaseFieldWrite,
): ResolvedWrite | null => {
  if ('arrangement' in write)
    return write.fieldKey === EDITOR_FIELD_NAME_OF_DECEASED
      ? { name_arrangement: write.arrangement }
      : null
  if ('rows' in write) {
    const form = tabularRowForm(write.fieldKey)
    return form
      ? caseColumnsWithTabularRows(form, caseRecord, write.rows, write.rowType)
      : null
  }
  const { fieldKey, value } = write
  const serviceTarget = parseServiceAuthorColumn(fieldKey)
  if (serviceTarget) {
    const wantsDateValue = serviceTarget.column === 'date'
    if (wantsDateValue !== value instanceof Date) return null
    const nextServices = servicesWithAuthoredColumn(
      caseRecord,
      serviceTarget.serviceId,
      serviceTarget.column,
      value instanceof Date ? value : trimWrittenCaseValue(value),
    )
    return nextServices ? { services: nextServices } : null
  }
  if (value instanceof Date) {
    const dateColumn = fieldKeyToDateColumn(fieldKey)
    return dateColumn ? { [dateColumn]: value } : null
  }
  const writable = fieldKeyToWritableCaseField(fieldKey)
  if (!writable) return null
  return {
    [writable.column]: writable.transform ? writable.transform(value) : value,
  }
}

export const useAuthorCaseFields = (): ((
  writes: readonly CaseFieldWrite[],
) => Promise<boolean>) =>
  useRecoilCallback(
    ({ snapshot, set }) =>
      async (writes: readonly CaseFieldWrite[]): Promise<boolean> => {
        if (writes.length === 0) return true

        const _case = await snapshot.getPromise(caseAtom)
        let caseRecord = (_case as Record<string, unknown> | null) ?? {}
        const caseId = (caseRecord as { id?: string }).id

        const patch: Record<string, unknown> = {}
        const touchedColumns = new Set<string>()
        for (const write of writes) {
          const resolved = resolveWrite(caseRecord, write)
          if (!resolved) return false
          for (const [column, stored] of Object.entries(resolved)) {
            caseRecord = { ...caseRecord, [column]: stored }
            patch[column] = stored
            touchedColumns.add(column)
            if (NAME_PART_COLUMNS.has(column)) {
              const flat = nameOfDeceasedFromCase(caseRecord)
              caseRecord = { ...caseRecord, name_of_deceased: flat }
              patch['name_of_deceased'] = flat
              touchedColumns.add('name_of_deceased')
            }
          }
        }

        set(caseAtom, caseRecord)
        for (const column of touchedColumns) {
          const stored = caseRecord[column]
          set(caseValuesAtom(column), (prev: unknown) => ({
            ...(typeof prev === 'object' && prev ? prev : { id: column }),
            value: stored,
          }))
        }
        if (caseId) {
          try {
            await updateCase(caseId, patch as Parameters<typeof updateCase>[1])
          } catch (err) {
            console.error(
              '[editor] case-field persist failed',
              Object.keys(patch),
              err,
            )
            return false
          }
        }
        return true
      },
    [],
  )

export const useAuthorCaseField = (): ((
  fieldKey: string,
  value: string | Date,
) => Promise<boolean>) => {
  const authorCaseFields = useAuthorCaseFields()
  return useCallback(
    (fieldKey: string, value: string | Date) =>
      authorCaseFields([{ fieldKey, value }]),
    [authorCaseFields],
  )
}

/** Writes a tabular field's rows to the case, the way its row form maps them onto the case; an additional-family group's rows replace that group's only. */
export const useAuthorCaseRows = (): ((
  fieldKey: string,
  rows: readonly TabularRow[],
  rowType?: string,
) => Promise<boolean>) => {
  const authorCaseFields = useAuthorCaseFields()
  return useCallback(
    (fieldKey: string, rows: readonly TabularRow[], rowType?: string) =>
      authorCaseFields([{ fieldKey, rows, rowType }]),
    [authorCaseFields],
  )
}

export default useAuthorCaseField
